<template>
  <div :id="chartId" class="container-chart bg-chart">
    <h6 class="title-font mt-2 mb-3" style="text-align: center">{{ title }}</h6>
    <div class="row mb-2" style="text-align: center">
      <div
        :class="indicatorColumnStyle"
        v-for="(item, index) in sortedData"
        :key="index"
        :style="fontColor(item)"
      >
        <label class="label-title mb-0" :style="fontColor(item)" v-if="item.sort != 'all'">{{
          getLabelTitle(item)
        }}</label>
        <div v-if="isLoaded">
          <h1 class="mb-0">{{ getItemValue(item) }}</h1>
          <h5 :style="subtitleStyle">{{ getItemUnit(item) }}</h5>
        </div>
        <div v-if="!isLoaded">
          <h1 style="visibility: hidden">0</h1>
          <h5 style="visibility: hidden">0</h5>
        </div>
        <span
          class="span-indicator-percentage"
          v-if="
            item.sort != 'all' &&
              dataset.dataType == 'count' &&
              dataset.dataValue != 'cartel' &&
              dataset.dataValue != 'hotspot' &&
              dataset.dataValue != 'interactiveobject' &&
              dataset.dataValue != 'hotspot' &&
              dataset.dataValue != 'immersive'
          "
          >({{ item.percent }} %)</span
        >
      </div>
    </div>
    <chart-error :dataLoadedError="dataLoadedError" @reload="reloadData"></chart-error>
    <div class="lds-ripple" v-if="isDataLoading">
      <div></div>
      <div></div>
    </div>
  </div>
</template>

<script>
import utils from '../../utils'
import { utilsMixin, chartMixin } from '../../utils/mixins'

import { getByteRound, getByteUnit, getByteUnitOverTime } from '../../utils/unit'

export default {
  name: 'IndicatorChart',
  dependencies: ['dataService'],
  mixins: [utilsMixin, chartMixin],
  data() {
    return {
      dataset: { value: '', unit: 'no' },
      sortedData: [{ sort: 'all', value: '', unit: 'no' }],
      isLoaded: false,
      savedStart: null,
      savedEnd: null,
      dataLoadedError: false,
    }
  },
  props: ['config', 'chartId', 'period', 'area', 'mode', 'previewDataset'],
  mounted() {
    if (this.mode != 'preview') {
      this.savedStart = this.period[0]
      this.savedEnd = this.period[1]
    } else {
      this.dataset = this.previewDataset
      this.displaySortedData()
    }
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  computed: {
    colors: function() {
      return utils.colorSet(this.config.sort_by, this.$store.getters['config/filtersConfig'])
    },
    title: function() {
      if (this.config) {
        return this.config.titles[this.$locale]
      } else {
        return ''
      }
    },
    subtitleStyle: function() {
      return this.sortedData.length > 6
        ? 'text-align: center;'
        : 'text-align: right; margin-right:1rem;'
    },
    indicatorColumnStyle: function() {
      return this.sortedData.length > 6 ? 'col-sm-2 mb-4' : 'col'
    },
  },
  components: {
    'chart-error': () => import('../utils/ChartErrorReload'),
  },
  watch: {
    config: {
      handler() {
        this.loadConfigAndFetchData()
      },
      immediate: true,
    },
    period: {
      handler: function(period) {
        if (this.mode != 'preview') {
          if (period[0] != this.savedStart || period[1] != this.savedEnd) {
            this.savedStart = this.period[0]
            this.savedEnd = this.period[1]
            this.loadConfigAndFetchData()
          }
        } else {
          this.displaySortedData()
        }
      },
      immediate: false,
    },
    area: {
      handler() {
        this.loadConfigAndFetchData()
      },
      immediate: false,
    },
  },
  methods: {
    loadConfigAndFetchData: function() {
      this.requestCount++

      this.dataService
        .getChartData(
          this.config,
          this.period,
          this.dateFormat,
          this.getCurrentUser,
          this.currentRequestId,
          this.$props.area,
        )
        .then(data => {
          this.$emit('update', {
            chart_id: this.config.id,
            data: JSON.parse(JSON.stringify(data.result)),
          })
          this.dataset = data.result
          this.displaySortedData()
          this.isLoaded = true
          this.dataLoadedError = false
          this.requestCount--
        })
        .catch(e => {
          if (e.message != 'cancel') {
            console.error('IndicatorChart error', e)
            this.dataLoadedError = true
          }
          this.requestCount--
        })

      this.currentRequestId = this.getRequestId(
        this.config,
        this.period,
        this.dateFormat,
        this.getCurrentUser,
      )
    },
    displaySortedData: function() {
      this.sortedData = []
      if (!this.config.sort_by || this.config.sort_by == 'all') {
        this.sortedData = [
          { sort: 'all', key: '', value: this.dataset.value, unit: this.dataset.unit },
        ]
      } else {
        let filteredResults = this.dataset[this.config.sort_by]
        if (Object.keys(filteredResults).length == 0) {
          filteredResults = {}
          this.configFilterHierarchy.filter_values[this.config.sort_by].forEach(filter => {
            filteredResults[filter] = 0
          })
        }
        for (const key in filteredResults) {
          this.sortedData.push({
            sort: this.config.sort_by,
            key: key,
            value: filteredResults[key],
            unit: this.dataset.unit,
            percent:
              this.dataset.value != 0
                ? Number((100 * filteredResults[key]) / this.dataset.value).toFixed(2)
                : 0,
          })
        }

        this.sortData()
      }
    },
    reloadData: function() {
      if (this.dataLoadedError) {
        this.loadConfigAndFetchData()
        this.dataLoadedError = false
      }
    },
    getItemValue: function(item) {
      if (item.value) {
        if (
          this.dataset.unit === 'byte' ||
          this.dataset.dataValue === 'max_memory_level_gradient'
        ) {
          return getByteRound(item.value)
        } else if (this.dataset.dataValue === 'min_level_gradient') {
          return Math.abs(item.value)
        } else {
          return item.value
        }
      } else {
        return 0
      }
    },
    getItemUnit: function(item) {
      if (this.dataset.dataValue === 'max_memory_level_gradient') {
        return getByteUnitOverTime(item.value)
      } else if (this.dataset.unit === 'byte') {
        return getByteUnit(item.value)
      } else {
        return this.$t('unit.' + item.unit)
      }
    },
    getLabelTitle: function(item) {
      if (
        item.sort === 'site_id' ||
        item.sort === 'language' ||
        item.sort === 'product' ||
        item.sort === 'warning_type'
      ) {
        return this.$t(`${item.sort}.${item.key}`)
      } else if (item.sort === 'site' || item.sort === 'site_location') {
        return this.$i18n.messages[this.$i18n.locale][item.sort][item.key]
      } else if (item.sort === 'histopad_rating' || item.sort === 'visit_rating') {
        return `${item.key}  ${Number(item.key) > 1 ? this.$t('stars') : this.$t('star')}`
      } else if (item.sort === 'collected_treasure_count') {
        return `${item.key}  ${Number(item.key) > 1 ? this.$t('treasures') : this.$t('treasure')}`
      } else {
        return item.key
      }
    },
    fontColor: function(item) {
      return this.config.sort_by !== 'all'
        ? 'color: ' + this.colors[item.key]
        : 'color: ' + this.colors['all']
    },
    sortData: function() {
      if (this.config.display_options && this.config.display_options.display_by) {
        let displayBy = this.config.display_options.display_by

        switch (displayBy) {
          case 'value_asc':
            if (this.dataset.dataValue !== 'min_level_gradient') {
              this.sortedData.sort((a, b) => {
                return a.value - b.value
              })
            } else {
              this.sortedData.sort((a, b) => {
                return b.value - a.value
              })
            }

            break
          case 'value_desc':
            if (this.dataset.dataValue !== 'min_level_gradient') {
              this.sortedData.sort((a, b) => {
                return b.value - a.value
              })
            } else {
              this.sortedData.sort((a, b) => {
                return a.value - b.value
              })
            }

            break
          case 'site_id_asc':
            this.sortedData.sort((a, b) => {
              return Number(a.key) - Number(b.key)
            })
            break
          case 'site_id_desc':
            this.sortedData.sort((a, b) => {
              return Number(b.key) - Number(a.key)
            })
            break
          case 'alpha_asc':
            this.sortedData.sort((a, b) => {
              let aKeyValue = this.$t(`${this.config.sort_by}.${a.key}`)
              let bKeyValue = this.$t(`${this.config.sort_by}.${b.key}`)
              if (aKeyValue > bKeyValue) return 1
              if (aKeyValue < bKeyValue) return -1
              else return 0
            })
            break
          case 'alpha_desc':
            this.sortedData.sort((a, b) => {
              let aKeyValue = this.$t(`${this.config.sort_by}.${a.key}`)
              let bKeyValue = this.$t(`${this.config.sort_by}.${b.key}`)
              if (aKeyValue > bKeyValue) return -1
              if (aKeyValue < bKeyValue) return 1
              else return 0
            })
            break
          default:
            this.sortedData.sort((a, b) => {
              return b.value - a.value
            })
            break
        }
      }
    },
  },
}
</script>
